import { Dispatch, SetStateAction } from 'react';
import { SystemData } from './types/SystemData';
import { PricingModulesStatus } from '../../context/contexts/PricingModuleContext';
import { isAnyEnablingModulesActive } from '../SystemSettings/hooks/lockedFeatures/utils';
import { RequiredModuleState } from '../SystemSettings/hooks/lockedFeatures/types';
import { FilterKeys, FilterOption, FilterValues, FilterDetails, FiltersObject, SearchableFilterDetails, FilterValue, FilterMultipleValuesCount } from './types/filters';

// needed because Object.keys doesn't work with generics, this is a generic version of this method
export const getObjectKeys = <T extends object>(obj: T): (keyof T)[] => {
    return Object.keys(obj) as (keyof T)[];
};

export const isAccountMatchingEnabled = (systemData?: SystemData): boolean => {
    return !!systemData?.Settings.AccountMatchingStrategy;
};

export const getUserTokensLimit = (systemData?: SystemData): number => {
    return systemData?.Settings.UserTokensLimit ?? 2;
};

export const isMfaModuleEnabled = (pricingModulesState: PricingModulesStatus | null): boolean => {
    if (!pricingModulesState) {
        return false;
    }

    return isAnyEnablingModulesActive({ mfaUsers: RequiredModuleState.ON_PREM_ENABLED }, pricingModulesState);
};

interface DropdownItem<TValue> {
    value: TValue;
    title: string;
    subtitle?: string;
    onClick?: (value?: TValue) => void;
}

export const filterAllValuesFilters = <TFilters extends FiltersObject>(
    filters: TFilters,
    multipleFilters: FilterMultipleValuesCount<TFilters>
): TFilters => {
    return getObjectKeys(filters).reduce((acc, key) => {
        if (key in multipleFilters && filters[key]?.length === multipleFilters[key]) {
            acc[key] = [] as TFilters[keyof TFilters];
        } else {
            acc[key] = filters[key];
        }
        return acc;
    }, {} as TFilters);
};

export const getDropdownOptionsFromFilterOptions = <TValue>(
    filterOptions: FilterOption<TValue>[],
    onClick?: (value?: TValue) => void
): DropdownItem<TValue>[] =>
    filterOptions.map((option) => {
        const item: DropdownItem<TValue> = { value: option.value, title: option.label };
        if (option.subtitle) {
            item.subtitle = option.subtitle;
        }
        if (onClick) {
            item.onClick = onClick;
        }
        return item;
    });

export const getSortedFiltersDetails = <TFilters extends FiltersObject>(filters: TFilters, filtersDetails: FilterDetails<TFilters>[]) =>
    [...filtersDetails].sort((firstFilter, secondFilter) => {
        if (filters[secondFilter.id]?.length) {
            return 1;
        }


        if (filters[firstFilter.id]?.length) {
            return -1;
        }

        return 0;
    });

export const isSearchableFilter = <TFilters extends FiltersObject>(filter: FilterDetails<TFilters>): filter is SearchableFilterDetails<TFilters> => {
    return 'searchHandler' in filter;
};

type SetFiltersState<TFilters extends FiltersObject> = Dispatch<SetStateAction<TFilters>>;
type SetFiltersDetailsState<TFilters extends FiltersObject> = Dispatch<SetStateAction<FilterDetails<TFilters>[]>>;

type PartialSearchableFilterDetails<TFilters extends FiltersObject> = Partial<SearchableFilterDetails<TFilters>>;

type OptionalFilterValuesUpdater<TFilters extends FiltersObject> = (filterId: FilterKeys<TFilters>, filterValues?: FilterValue<TFilters>) => void;
type FilterSearchTermUpdater<TFilters extends FiltersObject> = (filterId: FilterKeys<TFilters>, searchTerm: string) => void;
type FilterOptionClickHandler<TFilters extends FiltersObject> = (filterId: FilterKeys<TFilters>, value: FilterValues<TFilters>) => void;
type SearchableFilterRemoveHandler<TFilters extends FiltersObject> = (filterId: FilterKeys<TFilters>) => void;
type SearchableFilterSearchStateUpdater<TFilters extends FiltersObject> = (filterId: FilterKeys<TFilters>, state: PartialSearchableFilterDetails<TFilters>) => void;

interface Params<TFilters extends FiltersObject> {
    setFilters: SetFiltersState<TFilters>;
    setFiltersDetails: SetFiltersDetailsState<TFilters>;
}

interface ReturnType<TFilters extends FiltersObject> {
    setFilterValues: OptionalFilterValuesUpdater<TFilters>;
    filterSearchHandlers: FilterSearchHandlers<TFilters>;
}

export interface FilterSearchHandlers<TFilters extends FiltersObject> {
    setFilterSearchTerm: FilterSearchTermUpdater<TFilters>;
    handleFilterOptionClick: FilterOptionClickHandler<TFilters>;
    onSearchableFilterRemove: SearchableFilterRemoveHandler<TFilters>;
    onSearchableFilterChange: OptionalFilterValuesUpdater<TFilters>;
    onSearchableFilterClose: OptionalFilterValuesUpdater<TFilters>;
}

export const handleFiltersDetails = <TFilters extends FiltersObject>({ setFilters, setFiltersDetails }: Params<TFilters>): ReturnType<TFilters> => {
    return {
        setFilterValues: setFilterValues(setFilters),
        filterSearchHandlers: {
            setFilterSearchTerm: setFilterSearchTerm(setFiltersDetails),
            handleFilterOptionClick: handleFilterOptionClick(setFiltersDetails),
            onSearchableFilterRemove: onSearchableFilterRemove(setFilters, setFiltersDetails),
            onSearchableFilterChange: onSearchableFilterChange(setFilters, setFiltersDetails),
            onSearchableFilterClose: onSearchableFilterClose(setFiltersDetails)
        }
    };
};

export const setFilterValues = <TFilters extends FiltersObject>(
    setFilters: SetFiltersState<TFilters>
): OptionalFilterValuesUpdater<TFilters> => {
    return (filterId: FilterKeys<TFilters>, filterValues?: FilterValue<TFilters>) => {
        setFilters(({ [filterId]: prevFilterValue, ...prevFilters}) => {
            if (filterValues) {
                return {
                    ...prevFilters,
                    [filterId]: filterValues
                } as TFilters;
            }

            return prevFilters as TFilters;
        });
    };
};

const setFilterSearchTerm = <TFilters extends FiltersObject>(
    setFiltersDetails: SetFiltersDetailsState<TFilters>
): FilterSearchTermUpdater<TFilters> => {
    return (filterId: FilterKeys<TFilters>, searchTerm: string) => {
        setFiltersDetails((prevFiltersDetails) => {
            return prevFiltersDetails.map((filterDetails) => {
                if (filterDetails.id === filterId && isSearchableFilter(filterDetails)) {
                    filterDetails.searchHandler(searchTerm);
                    return { ...filterDetails, searchTerm };
                }
                return filterDetails;
            });
        });
    };
};

const setFilterSearchState = <TFilters extends FiltersObject>(
    setFiltersDetails: SetFiltersDetailsState<TFilters>
): SearchableFilterSearchStateUpdater<TFilters> => {
    return (filterId: FilterKeys<TFilters>, state: PartialSearchableFilterDetails<TFilters>) => {
        setFiltersDetails((prevFiltersDetails) => {
            return prevFiltersDetails.map((filterDetails) => {
                if (filterDetails.id === filterId && isSearchableFilter(filterDetails)) {
                    return { ...filterDetails, ...state };
                }
                return filterDetails;
            });
        });
    };
};

const handleFilterOptionClick = <TFilters extends FiltersObject>(
    setFiltersDetails: SetFiltersDetailsState<TFilters>
): FilterOptionClickHandler<TFilters> => {
    return (filterId: FilterKeys<TFilters>, value: FilterValues<TFilters>) => {
        setFiltersDetails((prevFiltersDetails) => {
            return prevFiltersDetails.map((filterDetails) => {
                if (filterDetails.id === filterId && isSearchableFilter(filterDetails)) {
                    if (filterDetails.selectedItems?.includes(value)) {
                        return {
                            ...filterDetails,
                            selectedItems: filterDetails.selectedItems.filter((item) => item !== value),
                            selectedOptions: filterDetails.selectedOptions?.filter((option) => option.value !== value)
                        };
                    }
                    const option = filterDetails.options.find((option) => option.value === value);
                    return {
                        ...filterDetails,
                        selectedItems: [...(filterDetails.selectedItems || []), value],
                        selectedOptions: [...(filterDetails.selectedOptions || []), option],
                        selectedOptionsHistory: [...(filterDetails.selectedOptionsHistory || []).filter((history) => history.value !== value), option]
                    };
                }
                return filterDetails;
            });
        });
    };
}

const onSearchableFilterRemove = <TFilters extends FiltersObject>(
    setFilters: SetFiltersState<TFilters>,
    setFiltersDetails: SetFiltersDetailsState<TFilters>
): SearchableFilterRemoveHandler<TFilters> => {
    return (filterId: FilterKeys<TFilters>) => {
        setFilterValues(setFilters)(filterId, undefined);
        setFilterSearchState(setFiltersDetails)(filterId, { searchTerm: undefined, selectedItems: undefined, selectedOptions: undefined });
    };
}

const onSearchableFilterChange = <TFilters extends FiltersObject>(
    setFilters: SetFiltersState<TFilters>,
    setFiltersDetails: SetFiltersDetailsState<TFilters>
): OptionalFilterValuesUpdater<TFilters> => {
    return (filterId: FilterKeys<TFilters>, filterValues?: FilterValue<TFilters>) => {
        setFilterValues(setFilters)(filterId, filterValues);
        onSearchableFilterClose(setFiltersDetails)(filterId, filterValues);
    };
}

const onSearchableFilterClose = <TFilters extends FiltersObject>(
    setFiltersDetails: SetFiltersDetailsState<TFilters>
): OptionalFilterValuesUpdater<TFilters> => {
    return (filterId: FilterKeys<TFilters>, filterValues?: FilterValue<TFilters>) => {
        setFiltersDetails((prevFiltersDetails) => {
            return prevFiltersDetails.map((filterDetails) => {
                if (filterDetails.id === filterId && isSearchableFilter(filterDetails)) {
                    filterDetails.searchHandler('');
                    let filterValuesArray: FilterValues<TFilters>[] | undefined = undefined;
                    if (filterValues) {
                        filterValuesArray = Array.isArray(filterValues) ? filterValues : [filterValues];
                    }

                    return {
                        ...filterDetails,
                        searchTerm: undefined,
                        selectedItems: filterValuesArray,
                        selectedOptions: filterValuesArray !== undefined ? filterDetails.selectedOptionsHistory?.filter((option) => filterValuesArray?.includes(option.value)) : undefined
                    };
                }
                return filterDetails;
            });
        });
    };
}
